import { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error?: Error;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Only log in development to reduce console noise
    if (process.env.NODE_ENV === 'development') {
      console.error('🚨 ErrorBoundary caught an error:', error, errorInfo);
    }
    
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  public render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }
      
      return (
        <div className="text-red-500 text-sm p-2 border border-red-200 rounded bg-red-50">
          <div className="font-medium">Something went wrong</div>
          {process.env.NODE_ENV === 'development' && (
            <div className="text-xs mt-1">{this.state.error?.message}</div>
          )}
          <button
            onClick={() => {
              this.setState({ hasError: false, error: undefined });
              // Force a small delay to prevent rapid re-renders
              setTimeout(() => {
                window.location.reload();
              }, 100);
            }}
            className="text-xs underline mt-1"
          >
            Try again
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

